import Framebus from "framebus";

import {
  SipToken,
  SipAccount,
  SipNetwork,
  SipAppSolanaProvider,
  PnlShareParams,
  RouteLocationRaw,
} from "./types";
import { promiseWithTimeout } from './utils'
import {
  PlatformService,
  PlatformBaseService,
  PlatformId,
  FRAME_MESSAGE_TYPE,
  ServiceOptions,
} from "./base";

const MESSAGE_NAMESPACE = "SIP_APP_FRAME";

const bus = new Framebus({
  channel: MESSAGE_NAMESPACE,
});

type BackgroundMessage = {
  method: 'enkrypt_unlock_keyring';
  params: [string, boolean];
};

export class AppWebViewService implements PlatformBaseService {
  platformId = PlatformId.AppWebView;
  provider: SipAppSolanaProvider
  options: ServiceOptions;

  constructor(options?: ServiceOptions) {
    this.options = { ...options }
    this.provider = window.sip?.providers?.solana as unknown as SipAppSolanaProvider
  }

  async heartbeat(count: number, needStat?: boolean): ReturnType<PlatformService['heartbeat']> {
    return {count:await this.provider.heartbeat(count, needStat)};
  }

  async getVersion(): ReturnType<PlatformService["getVersion"]> {
    return {version:this.provider.getVersion()};
  }

  async getAccount(): Promise<SipAccount> {
    const account = await promiseWithTimeout(this.provider.getAccount(), 'getAccount timeout');
    return account;
  }

  async getLocale(): ReturnType<PlatformService["getLocale"]> {
    const locale = await promiseWithTimeout(this.provider.getLocale(), 'getLocale timeout');
    return {locale};
  }

  async getFavoriteList(): ReturnType<PlatformService["getFavoriteList"]> {
    const favoriteList = await promiseWithTimeout(this.provider.getFavoriteList(), 'getFavoriteList timeout');
    return favoriteList;
  }

  async getRoute(): ReturnType<PlatformService["getRoute"]> {
    return {route:'' as any};
  }

  listenLocale(
    callback: (locale: string) => void
  ): ReturnType<PlatformService["listenLocale"]> {
    const handler = (data: any, relayHandler: any) => {
      callback(data.locale);
    };
    bus.on(FRAME_MESSAGE_TYPE.LISTEN_LOCALE, handler);

    return () => {
      bus.off(FRAME_MESSAGE_TYPE.LISTEN_LOCALE, handler);
    };
  }

  listenRouterPush(
    callback: (to: RouteLocationRaw) => void
  ): ReturnType<PlatformService["listenLocale"]> {
    const handler = (data: any, relayHandler: any) => {
      callback(data.to);
    };
    bus.on(FRAME_MESSAGE_TYPE.LISTEN_ROUTER_PUSH, handler);

    return () => {
      bus.off(FRAME_MESSAGE_TYPE.LISTEN_ROUTER_PUSH, handler);
    };
  }

  async getNetwork(): Promise<SipNetwork> {
    const defaultNetwork = {
      assets: [],
      basePath: "m/44'/501'",
      blockExplorerAddr: "https://solscan.io/account/[[address]]",
      blockExplorerTX: "https://solscan.io/tx/[[txHash]]",
      coingeckoID: "solana",
      coingeckoPlatform: "solana",
      currencyName: "SOL",
      currencyNameLong: "Solana",
      customTokens: false,
      decimals: 9,
      homePage: "https://solana.com/",
      icon: "/src/providers/solana/networks/icons/sol.png",
      isTestNetwork: false,
      name: "SOLANA",
      name_long: "Solana",
      node: "https://api.sip.trading/helius-api",
      provider: "solana",
      signer: ['ed25519-sol'],
    } as any
    const res = await bus.emitAsPromise<SipNetwork>(
      FRAME_MESSAGE_TYPE.GET_NETWORK,
      {}
    );
    return res || defaultNetwork;
  }

  async getTokenMap(): ReturnType<PlatformBaseService["getTokenMap"]> {
    const res = await bus.emitAsPromise<any>(
      FRAME_MESSAGE_TYPE.GET_TOKEN_MAP,
      {}
    );
    return res;
  }

  // @TODO: 这部分后续应该从 localStorage 中读取，如果 parent 需要用到的话在写具体 Method
  async getStorage(): ReturnType<PlatformBaseService["getStorage"]> {
    const res = await bus.emitAsPromise<any>(
      FRAME_MESSAGE_TYPE.GET_STORAGE,
      {}
    );
    return res;
  }

  async signMessage(
    message: string
  ): ReturnType<PlatformService["signMessage"]> {
    try {
      const { signature, error} = await promiseWithTimeout(this.provider.signMessage(message), 'Sign message timeout');
      if (!signature) {
        throw new Error("Signature is empty, please try again");
      }
      if (error) {
        throw new Error(error)
      }
      return signature;
    } catch (error:any) {
      throw new Error(error)
    }
  }

  async visitToken(token: SipToken): Promise<void> {
    await promiseWithTimeout(this.provider.visitToken(token), 'visitToken timeout');
  }

  async switchFavorite(token: SipToken): Promise<void> {
    await promiseWithTimeout(this.provider.switchFavorite(token), 'switchFavorite timeout');
  }

  async sendToBackgroundFromAction(message: string): Promise<any> {
    try {
      const params=JSON.parse(message) as BackgroundMessage
      switch(params.method){
        case 'enkrypt_unlock_keyring':
          const res = await promiseWithTimeout(this.provider.verifyPassword(params.params[0]), 'sendToBackgroundFromAction timeout');
          return { result: res};
        default:
          throw new Error('Invalid method');
      }
    } catch (error:any) {
      return { result:false};
    }
  }

  async pnlShare(params: PnlShareParams): Promise<void> {
    await promiseWithTimeout(this.provider.pnlShare(params), 'pnlShare timeout');
  }
}
